import {
    Accordion,
    AccordionItem,
    AccordionPanel,
    Skeleton,
    Stack,
    ThemeBorderVariable,
    ThemeColorVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useIsMutating } from "@tanstack/react-query";
import {
    useFilteredAvailableTimes,
    useFilteredExperiences,
} from "api/api-hooks/use-available-times";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { ActivityModal } from "components/booking-modal/activity-modal";
import { ExperienceModal } from "components/booking-modal/experience-modal";
import Summary from "components/selection-components/summary/summary";
import { getLocalizedValue } from "helpers/language-helper";
import i18n from "i18next";
import { useState } from "react";
import { AvailableTimesSection } from "types/available-times";
import { useTranslation } from "react-i18next";
import { Experience, getFamilyId } from "types/experience";

/**
 * Renders booking articles/summary with skeleton loading
 * Shows skeleton while booking data is loading, null when no articles exist
 */
function Articles() {
    const { data: booking, isLoading } = useWebBooking();
    const isCreatingBooking = useIsMutating({ mutationKey: ["create-booking"] }) > 0;
    const { t } = useTranslation();
    const [openActivity, setOpenActivity] = useState<AvailableTimesSection | null>(null);
    const date = booking?.start.clone().startOf("day");
    const filteredAvailableTimes = useFilteredAvailableTimes(date);
    const filteredExperiences = useFilteredExperiences(date);
    const [openExperience, setOpenExperience] = useState<Experience | null>(null);

    function handleEditActivity(ruleId: number) {
        const section = filteredAvailableTimes.find((s) =>
            s.timeSets.some((ts) => ts.id === ruleId)
        );
        if (section) setOpenActivity(section);
    }

    function handleEditExperience(experienceId: number) {
        // The summary addresses the family; the row that owns the times is often a copy, so an
        // id-only match falls through to a blank experience with no times (BOOK-3910).
        const experienceWithTimes = filteredExperiences.find(
            (exp) => exp.id === experienceId || getFamilyId(exp) === experienceId
        );
        const base = experienceWithTimes ?? new Experience({ id: experienceId });
        const lang = i18n.language;
        const name = getLocalizedValue(base.name);
        setOpenExperience(
            new Experience({
                ...base,
                name: { [lang]: name },
            })
        );
    }

    // Show skeleton while loading
    if (isCreatingBooking || isLoading || !booking) {
        return (
            <Stack>
                <Skeleton height="1.5rem" width="40%" />
                <Stack
                    border={`${ThemeBorderVariable.XSmall} ${ThemeColorVariable.OnSurfaceBorder}`}
                    borderRadius={ThemeRadiusVariable.Small}
                    p={ThemeSpaceVariable.Medium}
                    spacing={ThemeSpaceVariable.Medium}
                >
                    <Skeleton height="1rem" width="60%" />
                </Stack>
            </Stack>
        );
    }

    const rules = booking.webBookingRules ?? [];
    const uniqueNormalRuleIds = new Set(rules.filter((r) => !r.experienceId).map((r) => r.id));
    const uniqueExperienceIds = new Set(
        rules.filter((r) => r.experienceId).map((r) => r.experienceId)
    );
    const bookedActivitiesCount = uniqueNormalRuleIds.size + uniqueExperienceIds.size;

    // Return null when no articles to show
    if (!booking?.articles || !booking?.nonTableArticles) return null;

    const isRequest = booking.isInquiry();
    const title = isRequest
        ? t("bookingRequest.myRequest")
        : t("myBooking.title") + (bookedActivitiesCount > 1 ? ` (${bookedActivitiesCount})` : "");
    return (
        <>
            <Accordion variant="noBorder" defaultIndex={0}>
                <AccordionItem title={title}>
                    {/* @ts-expect-error The design requires no bottom padding, but `pb` is not yet declared on AccordionPanel's TypeScript interface. It still works at runtime because props are spread to the underlying panel in casper-ui and should be added to the component's interface in the library. */}
                    <AccordionPanel pb="0">
                        <Summary
                            onEditActivity={handleEditActivity}
                            onEditExperience={handleEditExperience}
                        />
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
            <ActivityModal
                section={openActivity}
                setSummaryOpen={() => {}}
                onClose={() => setOpenActivity(null)}
            />
            <ExperienceModal experience={openExperience} onClose={() => setOpenExperience(null)} />
        </>
    );
}

export { Articles };
